import React, { useState, useEffect } from "react";
import NavBar from "frontend/Components/DesignSystem/NavBar";
import HeroAnimation from "frontend/Components/HeroAnimation/HeroAnimation";
import { useNavigate } from "react-router-dom";
import "../../../css/Adviser/Pages/AdviserHomePage.css";
import CardProduct from "frontend/Components/DesignSystem/Cards/CardProduct";
import { ReactComponent as RosemountInMediaIcon } from "../../../assets/icons/Rosemount in Media.svg";
import { ReactComponent as HearFromOurAdvisersIcon } from "../../../assets/icons/hear from our advisers.svg";
import { ReactComponent as EventsIcon } from "../../../assets/icons/Events.svg";
import FAQContainer from "frontend/Components/DesignSystem/FAQ/FAQContainer";
import { ContactUs } from "frontend/Components/ContactForm/ContactUs";
import Footer from "frontend/Components/DesignSystem/Footer";
import AdviserValues from "frontend/Components/AdviserValues/AdviserValue";
import VideoCarouselPopup from "../../Components/DesignSystem/VideoCarousel";
import ButtonYellow from "frontend/Components/DesignSystem/Buttons/ButtonYellow";
import SeoHelmet from "frontend/Components/SEO/SeoHelmet";
import { SEO_PAGES, getPageUrl } from "frontend/Utils/seoConfig";
import ImageDirectory from "frontend/HelperFunctions/ImageDirectory";
import { getAdviserValues } from "frontend/Utils/GetAdviserValues";

/**
 * TechnologyPage component renders the technology information page for advisers.
 *
 * This page provides details about the technology stack and tools available to advisers,
 * including CRM systems, sourcing platforms, and protection research tools. It also features
 * navigation, hero animation, adviser values, FAQs, and links to related resources.
 *
 * State:
 * - isModalOpen: Controls the visibility of the video carousel popup modal.
 * - values: Stores adviser values fetched asynchronously.
 *
 * Effects:
 * - Fetches adviser values on mount and updates the state.
 *
 * Handlers:
 * - handleToggleModal: Toggles the video carousel popup modal.
 *
 * UI Structure:
 * - Helmet: Sets the page title and meta description.
 * - NavBar: Displays navigation items.
 * - HeroAnimation: Shows a hero section with an image and heading.
 * - OurGuide: Describes the technology platforms used.
 * - AdviserValues: Displays adviser values and a call-to-action button.
 * - FAQContainer: Lists frequently asked questions.
 * - Adviser-Services-Container: Shows cards for media, adviser testimonials, and events.
 * - VideoCarouselPopup: Modal for adviser testimonial videos.
 * - ContactUs: Contact form section.
 * - Footer: Page footer.
 *
 * @component
 * @returns {JSX.Element} The rendered TechnologyPage component.
 */
const TechnologyPage = () => {
  const navigationItems = [
    { label: "Home", navigateTo: "/Adviser" },
    { label: "Join Us", navigateTo: "/Adviser/JoinUs" },
    { label: "About Us", navigateTo: "/Adviser/AboutUs" },
    {
      label: "Contact Us",
      navigateTo: "/ContactUs",
      state: { isAdviser: true },
    },
  ];
  const [isModalOpen, setIsModalOpen] = useState(false);

  /**
   * Toggles the visibility state of the modal.
   * If the modal is currently open, it will be closed, and vice versa.
   *
   * @function
   * @returns {void}
   */
  const handleToggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };
  const [values, setValues] = useState([]); // start with an empty array

  useEffect(() => {
    const fetchValues = async () => {
      const data = await getAdviserValues(); // wait for the resolved array
      setValues(data);
    };

    fetchValues();
  }, []);
  const navigate = useNavigate();
  const technologyFaqs = [
    {
      question: "So what technology systems do you use?",
      answer:
        "Our primary CRM for all advisers is the Finplan CRM, provided by Twenty7tec. We also work with Twenty7tec for their mortgage sourcing solution, Velocity. For protection, we are integrated with Solution Builder.",
    },
    {
      question:
        "What does the future use of technology look like within Rosemount?",
      answer:
        "We have invested in our own internal development capacity, which allows us to have ultimate flexibility over what we create for our advisers. We have a clear roadmap of what tech we are creating for advisers, as we move into an age where tech and AI will need to be embraced, to ensure advisers maintain optimum efficiency when conducting business.",
    },
    {
      question:
        "I still like to use paper, does this mean you won't take me on?",
      answer:
        "At the core of our business, we still believe in simple, clear, traditional financial advice. Any tech that we use is geared towards making our business and our advisers more efficient. We don't mandate all technology use, and will work with you to make your process work for you.",
    },
  ];
  return (
    <div className="Adviser-Home-Page">
      <SeoHelmet
        page={SEO_PAGES.technology}
        service={{
          name: "Adviser Technology Support",
          description:
            "Rosemount Financial Solutions provides advisers with technology support including CRM systems, mortgage sourcing, protection research, cashflow modelling, ATR profiling and white-labelled client reports.",
          serviceType: [
            "Adviser Technology Support",
            "CRM Support",
            "Mortgage Sourcing Technology",
            "Protection Research Tools",
            "Cashflow Modelling",
            "Attitude to Risk Profiling",
            "White Labelled Client Reports",
          ],
        }}
        breadcrumbs={[
          {
            name: "Home",
            url: getPageUrl("/"),
          },
          {
            name: "Advisers",
            url: getPageUrl("/Adviser"),
          },
          {
            name: "Technology",
            url: getPageUrl("/Adviser/Technology"),
          },
        ]}
        faqs={technologyFaqs}
      />
      <NavBar navigationItems={navigationItems} />
      <HeroAnimation
        imgSrc={ImageDirectory["technology"]}
        isAdviser={true}
        altText="An adviser working behind a laptop, typing."
        children={
          <>
            <h1>Using the newest technology to assist your business</h1>
          </>
        }
      />

      <div className="Adviser-Personal-Approach-Content ">
        <h3>
          Technology is a vital component of allowing our advisers to work with
          their customers effectively and efficiently. We’ve a range of systems
          to use in place, including white labelled documents, our cashflow
          modeller and our attitude to risk (ATR) profiler.
        </h3>
        <h3>
          We have partnered with Twenty7tec to offer a complete CRM system,
          including electronic factfinds, full reporting suite, mortgage and
          protection sourcing, full client portal and more.
        </h3>
      </div>
      <div
        className="Events-Container Adviser-Technology-Container"
        // style={{ justifyContent: "center", alignItems: "center" }}
      >
        <div className="Events-Container-Content">
          <h3>Twenty7tec</h3>
          <h4>The sourcing system is fully integrated into our CRM system.</h4>
          <div className="Content-Large">
            We use the Twenty7tec Velocity sourcing platform, allowing advisers
            to quickly and compliantly source for mortgages, produce documents
            and progress to decisions in principle or full mortgage application.
            With the sourcing system integrated into the CRM system, this allows
            a streamlined and efficient process. We use SolutionBuilder for
            protection research. We also have a strong relationship with both
            LifeQuote and UndewriteMe, providing us a strong proposition for
            Protection. We provide you with the tolls to research the market for
            the best customer solutions. Our close relationship with a
            state-of-the-art electronic ID Verification system that makes the
            process of confirming the clients identity simple and easy.
          </div>
        </div>
        <div className="Image-Container">
          <img
            loading="eager"
            fetchPriority="high"
            src={ImageDirectory["27tech-logo"]}
            alt="27tech logo"
          />
        </div>
      </div>

      <div className="Events-Container Adviser-Technology-Container">
        <div className="Events-Container-Content">
          <h3>Cashflow Modeller</h3>
          <h4>
            Our Cashflow Modeller is free to use and available for our
            advisers.{" "}
          </h4>
          <div className="Content-Large">
            The Cashflow Modeller is designed to help clients make informed
            long-term financial decisions by showing the potential impacts of
            those decisions. The model presents pensions, investments, income
            and expenditure all in one place.
          </div>
          <div className="Content-Large">
            It also models real-life scenarios including ‘what if’ situations,
            tax-free cash withdrawals, extra contributions and market downturns.
            You can generate instant reports in seconds with graphs, notes and
            summaries for the client.
          </div>
          <div className="Content-Large">
            This means spending less time on manual spreadsheets and giving the
            client a clear view of their future. The final report will be white
            labelled which includes logo, company colour and regulatory text.
          </div>
        </div>
        <div className="Image-Container">
          <img
            loading="eager"
            fetchPriority="high"
            src={ImageDirectory["Cashflow Modeller"]}
            alt="Cashflow Modeller"
          />
        </div>
      </div>
      <div className="Events-Container Adviser-Technology-Container">
        <div className="Events-Container-Content">
          <h3>Attitude to Risk (ATR) Profiler</h3>
          <h4>
            Our ATR Profiler is designed to support advisers in client
            conversations around risk tolerance.
          </h4>
          <div className="Content-Large">
            This system is designed to assist our financial advisers when
            discussing their clients risk levels. This is based on their
            responses to a set of questions. There are six different risk
            profiles a client may be matched with.
          </div>
          <div className="Content-Large">
            As the client progresses through the questionnaire, the profiler
            updates in real-time. Upon completion, a fully white labelled PDF
            report can be generated, featuring your logo and company colours,
            client name, date, and a summary of their risk profile.
          </div>
        </div>
        <div className="Image-Container">
          <img
            loading="eager"
            fetchPriority="high"
            src={ImageDirectory["ATR Profiler"]}
            alt="ATR Profiler"
          />
        </div>
      </div>

      <AdviserValues
        values={values}
        isAdviser={true}
        title="Your Partner in Success"
        children={
          <ButtonYellow
            buttonText="Join Us"
            onClick={() => navigate("/Adviser/JoinUs")}
          />
        }
      />
      <FAQContainer
        isAdviser={true}
        faqs={[
          {
            question: "So what technology systems do you use?",
            answer:
              "Our primary CRM for all advisers is the Finplan CRM, provided by Twenty7tec. We also work with Twenty7tec for their mortgage sourcing solution, Velocity. For protection, we are integrated with Solution Builder.",
          },
          {
            question:
              "What does the future use of technology look like within Rosemount?",
            answer:
              "We have invested in our own internal development capacity, which allows us to have ultimate flexibility over what we create for our advisers. We have a clear roadmap of what tech we are creating for advisers, as we move into an age where tech and AI will need to be embraced, to ensure advisers maintain optimum efficiency when conducting business.",
          },
          {
            question:
              "I still like to use paper, does this mean you won't take me on?",
            answer:
              "At the core of our business, we still believe in simple, clear, traditional financial advice. Any tech that we use is geared towards making our business and our advisers more efficient. We don't mandate all technology use, and will work with you to make your process work for you.",
          },
        ]}
      />
      <div className="Adviser-Services-Container Adviser-Services-Holder">
        <CardProduct
          Icon={RosemountInMediaIcon}
          title="Rosemount in the Media"
          link="/RosemountInMedia"
          isAdviser={true}
        />
        <div onClick={handleToggleModal}>
          <CardProduct
            Icon={HearFromOurAdvisersIcon}
            title="Hear from our Advisers"
            isAdviser={true}
          />
        </div>
        <VideoCarouselPopup
          isOpen={isModalOpen}
          onClose={handleToggleModal}
          // videos={videos}
        />
        <CardProduct
          Icon={EventsIcon}
          title="Events"
          link="/Adviser/Events"
          isAdviser={true}
        />
      </div>

      <ContactUs isAdviser={true} />
      <Footer isAdviser={true} />
    </div>
  );
};

export default TechnologyPage;
